/**
 * Engraving UI Module
 * <engraving-selector> owns the engravings for a product on the PDP: it shows a summary card,
 * opens the <engraving-studio> popup to edit them, and exposes validate()/getSelectedEngravings()
 * to product-form.js, which attaches them to the cart line after add-to-cart.
 */

// Scoped so the file can load twice (quick view re-injects its scripts) without redeclaring.
(() => {
  class EngravingSelector extends HTMLElement {
    constructor() {
      super();
      this.productId = parseInt(this.dataset.productId);
      this.config = null;
      this.engravings = []; // [{ blade, side, text, font }]
    }

    connectedCallback() {
      this.config = window.engravingConfig?.[this.productId];
      if (!this.config || !this.config.enabled || !this.config.blades.length) {
        this.style.display = 'none';
        return;
      }

      this.studio = this.querySelector('engraving-studio');
      this.emptyState = this.querySelector('[data-engraving-empty]');
      this.filledState = this.querySelector('[data-engraving-filled]');
      this.list = this.querySelector('[data-engraving-list]');
      this.total = this.querySelector('[data-engraving-total]');

      this.querySelectorAll('[data-engraving-open]').forEach((button) =>
        button.addEventListener('click', () => this.openStudio())
      );
      this.querySelector('[data-engraving-remove]')?.addEventListener('click', () => {
        this.setEngravings([]);
      });

      this.render();
      this.initTypewriter();
    }

    /**
     * Phones: Personalize types its label out once when it first scrolls into view, then only the
     * caret keeps blinking (same effect as the sticky bar's Personalize button).
     */
    initTypewriter() {
      const wrapper = this.querySelector('[data-card-type]');
      const text = wrapper?.querySelector('[data-card-type-text]');
      const button = wrapper?.closest('button');
      const phone = window.matchMedia('(max-width: 749px)').matches;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!text || !button || !phone || reduceMotion || !('IntersectionObserver' in window)) return;

      const label = wrapper.querySelector('.engraving-card__type-ghost').textContent.replace(/\s+/g, ' ').trim();
      const observer = new IntersectionObserver((entries) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();

        // The ghost copy goes visibility:hidden while typing, so keep the accessible name on the button
        button.setAttribute('aria-label', label);
        wrapper.classList.add('is-typing');
        let index = 0;
        const timer = setInterval(() => {
          index += 1;
          text.textContent = label.slice(0, index);
          if (index >= label.length) {
            clearInterval(timer);
            wrapper.classList.remove('is-typing');
            wrapper.classList.add('is-typed');
          }
        }, 70);
      });
      observer.observe(wrapper);
    }

    openStudio() {
      if (!this.studio || !this.config) return;
      this.studio.open(this.config, this.engravings, (engravings) => {
        this.setEngravings(engravings);
        this.addToCart();
      });
    }

    /**
     * The studio CTA is the add-to-cart action. Reuse the normal product-form
     * submission so variant selection, cart rendering, engraving properties,
     * and the engraving service product stay on their established paths.
     */
    addToCart() {
      const productForm =
        this.closest('.product__info-container, .product__info-wrapper, quick-add-modal')?.querySelector('product-form') ||
        this.parentElement?.parentElement?.querySelector('product-form') ||
        document.querySelector('product-form');
      const submitButton = productForm?.querySelector('[type="submit"][name="add"]');
      if (submitButton && !submitButton.disabled) submitButton.click();
    }

    setEngravings(engravings) {
      this.engravings = engravings;
      this.render();
      this.dispatchChangeEvent();
    }

    render() {
      const hasEngravings = this.engravings.length > 0;
      this.emptyState.hidden = hasEngravings;
      this.filledState.hidden = !hasEngravings;
      if (!hasEngravings) return;

      const bladesById = new Map(this.config.blades.map((blade) => [blade.id, blade]));
      this.list.innerHTML = '';
      this.engravings.forEach((engraving) => {
        const blade = bladesById.get(engraving.blade);
        const item = document.createElement('li');
        item.className = 'engraving-card__item';
        const surface = document.createElement('span');
        surface.className = 'engraving-card__surface';
        surface.textContent = `Blade ${engraving.blade} · ${engraving.side === 'front' ? 'Front' : 'Back'}${blade ? ` (${blade.style})` : ''}`;
        const text = document.createElement('span');
        text.className = 'engraving-card__text';
        text.textContent = `“${engraving.text}”`;
        item.append(surface, text);
        this.list.appendChild(item);
      });

      const count = this.engravings.length;
      this.total.textContent = `${count} ${count === 1 ? 'surface' : 'surfaces'} · +${this.studio.formatMoney(count * this.studio.priceCents)}`;
    }

    /**
     * Engravings ready for the cart
     * @returns {Array} Array of engraving objects
     */
    getSelectedEngravings() {
      return this.engravings.filter((engraving) => engraving.text.trim().length > 0);
    }

    clearAllSelections() {
      this.setEngravings([]);
    }

    /**
     * Engraving is optional; the studio already caps text length, so this only re-checks limits.
     * @returns {Object} { valid: boolean, errors: Array }
     */
    validate() {
      const errors = [];
      this.getSelectedEngravings().forEach((engraving, index) => {
        if (engraving.text.length > 100) {
          errors.push(`Engraving ${index + 1} text exceeds 100 characters`);
        }
      });
      return { valid: errors.length === 0, errors };
    }

    dispatchChangeEvent() {
      this.dispatchEvent(
        new CustomEvent('engraving:change', {
          detail: { engravings: this.getSelectedEngravings(), productId: this.productId },
          bubbles: true,
        })
      );
    }
  }

  if (!customElements.get('engraving-selector')) {
    customElements.define('engraving-selector', EngravingSelector);
  }
})();
